import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  LinearProgress,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useOnboardingAuth } from '../../hooks/useOnboardingAuth';
import { vendorServicesService } from '../../services/vendorServicesService';
import CategoryTreeSelector from '../../components/CategoryTreeSelector/CategoryTreeSelector';

// S3 asset URL
const logoImg = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';


const OnboardingStep5: React.FC = () => {
  usePageTitle('Services Setup');
  
  const navigate = useNavigate();
  const { vendor, isLoading: authLoading } = useOnboardingAuth({ currentStep: 4 });
  
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('');
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate selections
    if (!selectedMainCategory) {
      toast.error('Please select a main category');
      return;
    }

    if (selectedSubCategories.length === 0) {
      toast.error('Please select at least one sub category');
      return;
    }

    if (selectedTypes.length === 0) {
      toast.error('Please select at least one type');
      return;
    }

    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    if (!vendor) {
      toast.error('No vendor session found. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call API to save category tree
      const response = await vendorServicesService.saveCategoryTree({
        vendorId: vendor.vendorId,
        mainCategory: selectedMainCategory,
        subCategories: selectedSubCategories,
        types: selectedTypes,
        items: selectedItems,
      });

      if (response.success) {
        toast.success('Services saved. Continue to occasions...');
        setTimeout(() => {
          navigate('/onboarding/step6');
        }, 1000);
      } else {
        // Don't show error here - let the catch block handle it
        setIsSubmitting(false);
      }
    } catch (error: any) {
      console.error('Error saving category tree:', error);
      
      // Handle specific error messages from backend
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to save category tree. Please try again.';
      
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding/step4');
  };

  const progress = 83; // 5 of 6 steps

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 0',
          width: '100vw'
        }}
      >
        <CircularProgress size={60} sx={{ color: '#67314f', marginBottom: '20px' }} />
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            color: '#181818',
            textAlign: 'center'
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  // Show form only if vendor is authenticated
  if (!vendor) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 0',
        width: '100vw'
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          width: '120px',
          height: '94px',
          marginBottom: '40px'
        }}
      >
        <Box
          component="img"
          src={logoImg}
          alt="Eventi Logo"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          width: '100%',
          maxWidth: '540px',
          padding: '0 20px'
        }}
      >
        {/* Title Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '44px',
              lineHeight: 1.15,
              letterSpacing: '-0.44px',
              color: '#181818',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            Complete profile
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: 1.4,
              color: '#181818',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            Complete your company Eventi profile to access your management dashboard. This will only take couple of minutes.
          </Typography>
          
          {/* Progress Indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                color: '#181818'
              }}
            >
              5 of 6
            </Typography>
          </Box>
          
          {/* Progress Bar */}
          <Box sx={{ width: '100%', margin: '0 auto' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: '4px',
                borderRadius: '2px',
                backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#67314f',
                  borderRadius: '2px'
                }
              }}
            />
          </Box>
        </Box>

        {/* Form Section */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}
        >
          <CategoryTreeSelector
            selectedMainCategory={selectedMainCategory}
            selectedSubCategories={selectedSubCategories}
            selectedTypes={selectedTypes}
            selectedItems={selectedItems}
            onMainCategoryChange={setSelectedMainCategory}
            onSubCategoryChange={setSelectedSubCategories}
            onTypeChange={setSelectedTypes}
            onItemChange={setSelectedItems}
            showMainCategory={true}
          />

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginTop: '16px'
            }}
          >
            {/* Continue Button */}
            <Button
              type="submit"
              fullWidth
              disabled={isSubmitting}
              sx={{
                height: '56px',
                backgroundColor: '#67314f',
                borderRadius: '16px',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                letterSpacing: '-0.32px',
                textTransform: 'none',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#5a2a42'
                },
                '&:disabled': {
                  backgroundColor: '#93989c',
                  color: 'white'
                }
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Next'
              )}
            </Button>

            {/* Back Button */}
            <Button
              onClick={handleBack}
              fullWidth
              startIcon={<ArrowBack />}
              sx={{
                height: '56px',
                backgroundColor: 'transparent',
                borderRadius: '16px',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                letterSpacing: '-0.32px',
                textTransform: 'none',
                color: '#67314f',
                '&:hover': {
                  backgroundColor: 'rgba(103, 49, 79, 0.04)'
                },
                '& .MuiButton-startIcon': {
                  marginRight: '8px'
                }
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingStep5;
